import {Box, Collapse, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {isEmpty} from 'lodash';

export interface EvidenceSourceManageValue {
  manageProjectEvidence?: boolean;
  uploadRights?: boolean;
  initiateProcessingRights?: boolean;
}
export interface EvidenceSourceManageProps {
  initEvidenceSourceManageValue?: EvidenceSourceManageValue;
  isDisabled?: boolean;
  onChangeValue?: (value: EvidenceSourceManageValue) => void;
  onChangeEvidenceSourceValue?: (value: EvidenceSourceManageValue) => void;
}

interface EvidenceSourceSwitchLabelProps {
  value: boolean;
  label: string;
  disabled: boolean;
  dataTestId: string;
  onChangeStateValue: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => void;
}

const EvidenceSourceManageView: React.FC<EvidenceSourceManageProps> = (
  props: EvidenceSourceManageProps
) => {
  const {
    initEvidenceSourceManageValue = {},
    isDisabled = false,
    onChangeValue,
    onChangeEvidenceSourceValue,
  } = props;

  const [evidenceSourceManageValue, setEvidenceSourceManageValue] =
    useState<EvidenceSourceManageValue>(initEvidenceSourceManageValue);

  const [enableEvidenceSourceManagement, setEnableEvidenceSourceManagement] =
    useState<boolean>(false);
  useEffect(() => {
    if (!isEmpty(initEvidenceSourceManageValue)) {
      setEvidenceSourceManageValue(initEvidenceSourceManageValue);
      const {manageProjectEvidence, uploadRights, initiateProcessingRights} =
        initEvidenceSourceManageValue;

      const isEnabled =
        !!manageProjectEvidence || !!uploadRights || !!initiateProcessingRights;

      setEnableEvidenceSourceManagement(isEnabled);
    }
  }, [initEvidenceSourceManageValue]);

  const updateEvidenceSourceManageValue = useCallback(
    (value: Partial<EvidenceSourceManageValue>) =>
      setEvidenceSourceManageValue((prevValue: EvidenceSourceManageValue) => {
        const updatedValue = {
          ...prevValue,
          ...value,
        };
        onChangeEvidenceSourceValue?.(updatedValue);
        return updatedValue;
      }),
    [onChangeEvidenceSourceValue]
  );

  const handleEvidenceSourceManagementToggle = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setEnableEvidenceSourceManagement(checked);

      if (!checked) {
        const resetValue = {
          manageProjectEvidence: false,
          uploadRights: false,
          initiateProcessingRights: false,
        };

        setEvidenceSourceManageValue(resetValue);
        onChangeValue?.(resetValue);
        onChangeEvidenceSourceValue?.(resetValue);
      }
    },
    [onChangeValue, onChangeEvidenceSourceValue]
  );

  const onChangeManageProjectEvidence = useCallback(
    (
      _: React.ChangeEvent<HTMLInputElement>,
      manageProjectEvidence: boolean
    ) => {
      const {uploadRights, initiateProcessingRights} =
        evidenceSourceManageValue;

      updateEvidenceSourceManageValue({
        manageProjectEvidence,
        ...(!manageProjectEvidence &&
          !uploadRights && {
            initiateProcessingRights: !(
              !manageProjectEvidence && !uploadRights
            ),
          }),
      });

      onChangeValue?.({
        uploadRights,
        manageProjectEvidence,
        initiateProcessingRights:
          !manageProjectEvidence && !uploadRights
            ? false
            : initiateProcessingRights,
      });
    },
    [evidenceSourceManageValue, onChangeValue, updateEvidenceSourceManageValue]
  );

  const onChangeUploadRights = useCallback(
    (_: React.ChangeEvent<HTMLInputElement>, uploadRights: boolean) => {
      const {manageProjectEvidence, initiateProcessingRights} =
        evidenceSourceManageValue;

      updateEvidenceSourceManageValue({
        uploadRights,
        ...(!manageProjectEvidence &&
          !uploadRights && {
            initiateProcessingRights: !(
              !manageProjectEvidence && !uploadRights
            ),
          }),
      });

      onChangeValue?.({
        manageProjectEvidence,
        uploadRights,
        initiateProcessingRights:
          !manageProjectEvidence && !uploadRights
            ? false
            : initiateProcessingRights,
      });
    },
    [evidenceSourceManageValue, onChangeValue, updateEvidenceSourceManageValue]
  );

  const onChangeInitiateProcessingRights = useCallback(
    (
      _: React.ChangeEvent<HTMLInputElement>,
      initiateProcessingRights: boolean
    ) => {
      const {manageProjectEvidence, uploadRights} = evidenceSourceManageValue;

      updateEvidenceSourceManageValue({
        initiateProcessingRights,
      });

      onChangeValue?.({
        manageProjectEvidence,
        initiateProcessingRights,
        uploadRights,
      });
    },
    [evidenceSourceManageValue, onChangeValue, updateEvidenceSourceManageValue]
  );

  const renderSwitchLabel = useCallback(
    (switchProps: EvidenceSourceSwitchLabelProps) => {
      const {value, label, disabled, dataTestId, onChangeStateValue} =
        switchProps;
      return (
        <Box className={'switch-label-container'}>
          <AurSwitch
            value={value}
            disabled={disabled}
            onChange={onChangeStateValue}
            dataTestId={dataTestId}
          />
          <Typography
            className={`typo-text subhead4_size ${disabled ? 'grey-700' : 'mist_1300'}`}
          >
            {label}
          </Typography>
        </Box>
      );
    },
    []
  );

  const {
    manageProjectEvidence = false,
    uploadRights = false,
    initiateProcessingRights = false,
  } = useMemo(() => evidenceSourceManageValue, [evidenceSourceManageValue]);

  return (
    <Box className={'evidence-source-manage-container'}>
      <Box className={'evidence-source-manage-desc'}>
        <Typography className="typo-text subhead2_size title-text">
          {t('eca.permisssionPanel.evidenceSourceManagement.title')}
        </Typography>
        <Typography
          className="typo-text body2_size subtitle-text"
          data-testid="subtitle"
        >
          {isDisabled
            ? t(
                'eca.permisssionPanel.evidenceSourceManagement.disabledSubtitle'
              )
            : t('eca.permisssionPanel.evidenceSourceManagement.subtitle')}
        </Typography>
        <Box
          className={`enable-switch-container ${!enableEvidenceSourceManagement ? 'no-border' : ''}`}
        >
          <div className="switch-on-row">
            <AurSwitch
              checked={enableEvidenceSourceManagement}
              onChange={handleEvidenceSourceManagementToggle}
              dataTestId="toggle-evidenceSourceManagement-switch"
              ariaLabel={`${t('aria.toggle')} ${t(
                'eca.permisssionPanel.evidenceSourceManagement.title'
              )}`}
            />
            <Typography className="typo-text subhead4_size" aria-live="polite">
              {`${t(
                'eca.permisssionPanel.' +
                  (enableEvidenceSourceManagement ? 'On' : 'Off')
              )}`}
            </Typography>
          </div>
        </Box>
      </Box>
      <Collapse
        in={enableEvidenceSourceManagement}
        timeout="auto"
        unmountOnExit
      >
        <Box className="evidence-source-switch-div">
          {renderSwitchLabel({
            value: manageProjectEvidence,
            label: t(
              'eca.permisssionPanel.evidenceSourceManagement.manageProjectEvidence'
            ),
            disabled: isDisabled,
            dataTestId: 'toggle-manageProjectEvidence-switch',
            onChangeStateValue: onChangeManageProjectEvidence,
          })}
          {renderSwitchLabel({
            value: uploadRights,
            label: t(
              'eca.permisssionPanel.evidenceSourceManagement.uploadRights'
            ),
            disabled: isDisabled,
            dataTestId: 'toggle-uploadRights-switch',
            onChangeStateValue: onChangeUploadRights,
          })}
          {renderSwitchLabel({
            value: initiateProcessingRights,
            label: t(
              'eca.permisssionPanel.evidenceSourceManagement.initiateProcessingRights'
            ),
            disabled: isDisabled || (!manageProjectEvidence && !uploadRights),
            dataTestId: 'toggle-initiateProcessingRights-switch',
            onChangeStateValue: onChangeInitiateProcessingRights,
          })}
        </Box>
      </Collapse>
    </Box>
  );
};

export default EvidenceSourceManageView;
